import React, { useState, useEffect, useMemo } from 'react';
import { Icons } from '../Icons';
import { Appointment, Patient, Dentist, Procedure } from '../../types';
import { APPT_STATUS } from '../../constants/appointmentStatus';
import {
  checkAppointmentConflict,
  suggestAlternativeSlots,
  formatTimeSlot,
  getEndTime,
  findAvailableSlots,
  type TimeSlot
} from '../../utils/appointmentUtils';

interface AppointmentModalProps {
  isOpen: boolean;
  editingAppt: Appointment | null;
  apptForm: Omit<Appointment, 'id' | 'startTime'>;
  patients: Patient[];
  dentists: Dentist[];
  procedures: Procedure[];
  rooms: string[];
  appointments: Appointment[];
  lockedPatientId?: string;
  allowStatusEdit?: boolean;
  isPatient?: boolean;
  onClose: () => void;
  onSave: (e: React.FormEvent) => void;
  onFormChange: (form: Omit<Appointment, 'id' | 'startTime'>) => void;
}

export const AppointmentModal: React.FC<AppointmentModalProps> = ({
  isOpen,
  editingAppt,
  apptForm,
  patients,
  dentists,
  procedures,
  rooms,
  appointments,
  lockedPatientId,
  allowStatusEdit = true,
  isPatient = false,
  onClose,
  onSave,
  onFormChange,
}) => {
  const [conflict, setConflict] = useState<ReturnType<typeof checkAppointmentConflict>>({ hasConflict: false });
  const [suggestedSlots, setSuggestedSlots] = useState<TimeSlot[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const conflictingAppt = conflict.conflictingAppointment;
  const conflictingPatient = conflictingAppt
    ? patients.find(p => p.id === conflictingAppt.patientId)
    : null;
  const conflictingStatus = conflictingAppt ? APPT_STATUS[conflictingAppt.status] : null;

  // Check for conflicts whenever form changes
  useEffect(() => {
    if (!apptForm.date || !apptForm.time || !apptForm.providerId || !apptForm.room) {
      setConflict({ hasConflict: false });
      return;
    }

    const conflictResult = checkAppointmentConflict(
      apptForm,
      appointments,
      editingAppt?.id
    );

    setConflict(conflictResult);

    if (conflictResult.hasConflict) {
      const suggestions = suggestAlternativeSlots(
        apptForm.date,
        apptForm.providerId,
        apptForm.room,
        apptForm.duration,
        appointments,
        5
      );
      setSuggestedSlots(suggestions);
    }
  }, [apptForm.date, apptForm.time, apptForm.providerId, apptForm.room, apptForm.duration, appointments, editingAppt?.id]);

  const handleSlotSelect = (slot: TimeSlot) => {
    onFormChange({
      ...apptForm,
      date: slot.date,
      time: slot.time,
    });
    setShowSuggestions(false);
  };

  const getDentistName = (id: string) => {
    return dentists.find(d => d.id === id)?.name || id;
  };

  // Next 7 days availability for the selected provider/room
  const slotsByDay = useMemo(() => {
    const providerId = apptForm.providerId || dentists[0]?.id;
    const room = apptForm.room || rooms[0];
    const duration = apptForm.duration || 30;
    if (!providerId || !room) return [];

    const days: { date: string; slots: TimeSlot[] }[] = [];
    const baseDateStr = apptForm.date || new Date().toISOString().split('T')[0];
    const dateStr = new Date(baseDateStr).toISOString().split('T')[0];
    const slots = findAvailableSlots(
      dateStr,
      providerId,
      room,
      duration,
      appointments,
      8,
      18,
      duration
    );
    days.push({ date: dateStr, slots: slots.slice(0, 12) });
    return days;
  }, [apptForm.providerId, apptForm.room, apptForm.duration, apptForm.date, appointments, dentists, rooms]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[400] bg-slate-900/60 backdrop-blur-md flex items-center justify-center p-6 animate-fadeIn">
      <div className="bg-white rounded-[3rem] w-full max-w-5xl shadow-2xl my-6 max-h-[90vh] flex flex-col animate-slideUp">
        <div className="p-8 border-b flex items-center justify-between bg-white rounded-t-[3rem] flex-shrink-0">
          <h3 className="text-2xl font-black">
            {editingAppt ? 'Edit Appointment' : 'New Appointment'}
          </h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-red-500">
            <Icons.X />
          </button>
        </div>

        <form onSubmit={onSave} className="p-8 space-y-6 overflow-y-auto flex-1">
          {/* Walk-in toggle */}
          <div className="flex items-center justify-between p-4 bg-orange-50 rounded-2xl">
            <span className="font-bold text-sm">Walk-In Appointment</span>
            <input
              type="checkbox"
              checked={apptForm.isWalkIn}
              onChange={e => onFormChange({ ...apptForm, isWalkIn: e.target.checked })}
              className="w-12 h-6"
            />
          </div>

          <div>
            <label className="text-xs font-bold text-slate-400 uppercase mb-2 block">Patient *</label>
            <select
              required
              className="w-full border-2 rounded-xl p-3 font-bold"
              value={apptForm.patientId}
              onChange={e => onFormChange({ ...apptForm, patientId: e.target.value })}
              disabled={!!lockedPatientId}
            >
              {!lockedPatientId && <option value="">Select Patient...</option>}
              {patients.map(p => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          {!isPatient && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-slate-400 uppercase mb-2 block">Procedure *</label>
                <select
                  className="w-full border-2 rounded-xl p-3 font-bold"
                  value={apptForm.type}
                  onChange={e => {
                    const proc = procedures.find(p => p.name === e.target.value);
                    onFormChange({
                      ...apptForm,
                      type: e.target.value,
                      duration: proc?.defaultDuration || 30,
                    });
                  }}
                  disabled={false}
                >
                  {procedures.map(p => (
                    <option key={p.id} value={p.name}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-400 uppercase mb-2 block">Duration (min)</label>
                <input
                  type="number"
                  min="5"
                  step="5"
                  className="w-full border-2 rounded-xl p-3 font-bold"
                  value={apptForm.duration}
                  onChange={e => onFormChange({ ...apptForm, duration: parseInt(e.target.value) })}
                  disabled={false}
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-slate-400 uppercase mb-2 block">Provider *</label>
              <select
                className="w-full border-2 rounded-xl p-3 font-bold"
                value={apptForm.providerId}
                onChange={e => onFormChange({ ...apptForm, providerId: e.target.value })}
                disabled={isPatient && !!lockedPatientId}
              >
                {dentists.map(d => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>

            {!isPatient && (
              <div>
                <label className="text-xs font-bold text-slate-400 uppercase mb-2 block">Room *</label>
                <select
                  className="w-full border-2 rounded-xl p-3 font-bold"
                  value={apptForm.room}
                  onChange={e => onFormChange({ ...apptForm, room: e.target.value })}
                >
                  {rooms.map(room => (
                    <option key={room} value={room}>
                      {room}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-slate-400 uppercase mb-2 block">Date *</label>
              <input
                type="date"
                className="w-full border-2 rounded-xl p-3 font-bold"
                value={apptForm.date}
                onChange={e => onFormChange({ ...apptForm, date: e.target.value })}
              />
            </div>

            <div>
              <label className="text-xs font-bold text-slate-400 uppercase mb-2 block">Time *</label>
              <input
                type="time"
                className="w-full border-2 rounded-xl p-3 font-bold"
                value={apptForm.time}
                onChange={e => onFormChange({ ...apptForm, time: e.target.value })}
              />
            </div>
          </div>

          {/* Quick availability grid */}
          <div className="p-4 bg-slate-50 rounded-2xl space-y-3 border">
            <div className="flex items-center justify-between">
              <p className="text-sm font-bold text-slate-700">
                Available slots for {getDentistName(apptForm.providerId)}
              </p>
              <span className="text-xs text-slate-500">Tap a time to fill date & time</span>
            </div>
            {slotsByDay.length === 0 ? (
              <p className="text-xs text-slate-400">Select a provider to see available slots.</p>
            ) : (
              <div
                className={`grid ${slotsByDay.length === 1 ? 'grid-cols-1' : 'grid-cols-2'} gap-3 max-h-64 overflow-y-auto w-full`}
              >
                {slotsByDay.map(day => (
                  <div key={day.date} className="bg-white border rounded-xl p-3 space-y-2 w-full">
                    <p className="text-xs font-bold text-slate-500">
                      {new Date(day.date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                    </p>
                    {day.slots.length === 0 ? (
                      <p className="text-[11px] text-slate-400">No availability</p>
                    ) : (
                      <div className="grid grid-cols-4 sm:grid-cols-8 lg:grid-cols-12 gap-2 w-full">
                        {day.slots.map(slot => (
                          <button
                            type="button"
                            key={slot.time}
                            onClick={() => onFormChange({ ...apptForm, date: slot.date, time: slot.time })}
                            className={`px-3 py-2 rounded-lg text-[11px] font-bold border text-center ${
                              apptForm.date === slot.date && apptForm.time === slot.time
                                ? 'bg-blue-600 text-white border-blue-700'
                                : 'border-slate-200 hover:border-blue-200 hover:bg-blue-50'
                            }`}
                          >
                            {slot.time}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Conflict Warning */}
          {conflict.hasConflict && (
            <div className="p-4 bg-red-50 border-2 border-red-200 rounded-2xl space-y-3">
              <div className="flex items-start space-x-3">
                <div className="text-red-600 mt-1">
                  <Icons.AlertCircle />
                </div>
                <div className="flex-1">
                  <h4 className="font-black text-red-900 text-sm mb-1">Scheduling Conflict Detected</h4>
                  <p className="text-sm text-red-700">
                    {conflictingAppt
                      ? `Conflicts with ${conflictingPatient?.name || 'another patient'} on ${conflictingAppt.date} from ${conflictingAppt.time} to ${getEndTime(conflictingAppt)} in ${conflictingAppt.room} with ${getDentistName(conflictingAppt.providerId)}.`
                      : conflict.message}
                  </p>

                  {conflictingAppt && (
                    <div className="mt-3 p-3 bg-white border border-red-100 rounded-2xl flex items-start justify-between">
                      <div>
                        <p className="font-bold text-sm">{conflictingPatient?.name || 'Unknown patient'}</p>
                        <p className="text-xs text-slate-600">
                          {conflictingAppt.date} · {conflictingAppt.time} - {getEndTime(conflictingAppt)} · {conflictingAppt.room}
                        </p>
                      </div>
                      {conflictingStatus && (
                        <span className={`text-[10px] font-bold px-2 py-1 rounded-lg ${conflictingStatus.color}`}>
                          {conflictingStatus.label}
                        </span>
                      )}
                    </div>
                  )}

                  {suggestedSlots.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setShowSuggestions(!showSuggestions)}
                      className="mt-3 text-xs font-bold text-red-600 hover:text-red-800 underline"
                    >
                      {showSuggestions ? 'Hide' : 'View'} Available Time Slots ({suggestedSlots.length})
                    </button>
                  )}
                </div>
              </div>

              {/* Suggested Slots */}
              {showSuggestions && suggestedSlots.length > 0 && (
                <div className="mt-3 space-y-2">
                  <p className="text-xs font-bold text-red-900 uppercase">Suggested Available Slots:</p>
                  <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto">
                    {suggestedSlots.map((slot, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => handleSlotSelect(slot)}
                        className="p-3 bg-white border-2 border-red-200 hover:border-red-400 hover:bg-red-50 rounded-xl text-left transition-all group"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-bold text-slate-900">{formatTimeSlot(slot)}</p>
                            <p className="text-xs text-slate-500">
                              {getDentistName(slot.providerId || '')} • {slot.room}
                            </p>
                          </div>
                          <div className="text-red-600 group-hover:text-red-700">
                            <Icons.ChevronRight />
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {!isPatient && (
            <div>
              <label className="text-xs font-bold text-slate-400 uppercase mb-2 block">Internal Notes</label>
              <textarea
                className="w-full border-2 rounded-xl p-3 h-24 resize-none"
                placeholder="Staff-only notes..."
                value={apptForm.internalNotes}
                onChange={e => onFormChange({ ...apptForm, internalNotes: e.target.value })}
              />
            </div>
          )}

          <div>
            <label className="text-xs font-bold text-slate-400 uppercase mb-2 block">Patient Notes</label>
            <textarea
              className="w-full border-2 rounded-xl p-3 h-24 resize-none"
              placeholder="Instructions for patient..."
              value={apptForm.patientNotes}
              onChange={e => onFormChange({ ...apptForm, patientNotes: e.target.value })}
            />
          </div>

          {editingAppt && allowStatusEdit && (
            <div>
              <label className="text-xs font-bold text-slate-400 uppercase mb-3 block">Status</label>
              <div className="grid grid-cols-4 gap-2">
                {Object.entries(APPT_STATUS).map(([key, status]) => {
                  const StatusIcon = Icons[status.icon as keyof typeof Icons];
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => onFormChange({ ...apptForm, status: key as any })}
                      className={`p-3 rounded-xl border-2 transition-all text-[11px] font-black flex flex-col items-center gap-1 ${
                        apptForm.status === key
                          ? status.color + ' border-current shadow-sm'
                          : 'border-slate-200 text-slate-400 bg-slate-50'
                      }`}
                    >
                      <StatusIcon />
                      <p>{status.label}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="pt-4 border-t sticky bottom-0 bg-white -mx-8 px-8 -mb-8 pb-8 rounded-b-[3rem]">
            <button
              type="submit"
              className="w-full py-4 bg-blue-600 text-white rounded-2xl font-black hover:bg-blue-700"
            >
              {editingAppt ? 'Save Changes' : 'Confirm Booking'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
